/**
 * CustomerHome — top-of-funnel home page composed of revenue-driving rails.
 *
 * Layout (top → bottom):
 *   1. ServiceTabs (Grocery / Food / Rides) — sticky under the navbar
 *   2. HeroBanner   — full-width promo carousel (auto-rotating)
 *   3. SocialProofStrip — ⭐ rating · 10K+ orders · ⚡ 12min avg · 🌿 fresh
 *   4. CategoryGrid — "Shop by category" tile grid
 *   5. CouponsStrip — taps-to-copy promo codes
 *   6. DealsStrip   — items at 15%+ off
 *   7. BuyAgainRail — only when the user is authed & has orders
 *   8. NearbyStoresRail — open-now nearby stores
 *   9. TrendingNowRail — bestsellers / newest
 *  10. QuickHighlights — trio of trust pillars
 *
 * Rides users are routed straight to /rides/:type by ServiceTabs; the
 * grocery layout is otherwise the canonical home.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { categoriesAPI, Category } from '../api/categories';
import { storesAPI, Store } from '../api/stores';
import { useAuthStore } from '../stores/authStore';
import HeroBanner from '../components/HeroBanner';
import ServiceTabs, { ServiceTab } from '../components/home/ServiceTabs';
import CategoryGrid from '../components/home/CategoryGrid';
import DealsStrip from '../components/home/DealsStrip';
import BuyAgainRail from '../components/home/BuyAgainRail';
import TrendingNowRail from '../components/home/TrendingNowRail';
import NearbyStoresRail from '../components/home/NearbyStoresRail';
import CouponsStrip from '../components/home/CouponsStrip';
import SocialProofStrip from '../components/home/SocialProofStrip';
import QuickHighlights from '../components/home/QuickHighlights';
import { Skeleton } from '../design';
import PageHead from '../components/seo/PageHead';
import '../styles/blinkit.css';

const CustomerHome: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();

  const [activeTab, setActiveTab] = useState<ServiceTab>('grocery');
  const [categories, setCategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [cats, sts] = await Promise.all([
          categoriesAPI.listCategories().catch(() => [] as Category[]),
          storesAPI.listStores(0, 10).catch(() => [] as Store[]),
        ]);
        if (cancelled) return;
        setCategories(cats);
        setStores(sts);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Limit grid to keep the page scannable on mobile.
  const topCategories = useMemo(() => categories.slice(0, 12), [categories]);

  return (
    <div className="blinkit-page" style={{ paddingBottom: 20, marginTop: -16 /* hug the navbar */ }}>
      <PageHead
        title="ProvisionMart — Fresh groceries delivered in minutes"
        description="Order fresh groceries, meat, and dishes from nearby stores. Free delivery on orders above ₹299 · Avg ETA 12 min · 4.7★ rated."
        image="/pmlogo.png"
        type="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'ProvisionMart',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://provisionmart.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: '/?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <ServiceTabs activeKey={activeTab} onChange={setActiveTab} />

      <Container style={{ maxWidth: 1280, padding: '0 8px' }}>
        {/* Hero */}
        <div style={{ marginBottom: 20 }}>
          <HeroBanner />
        </div>

        {/* Trust strip — establishes "this is real, reliable" within the first scroll. */}
        <SocialProofStrip />

        {/* Categories */}
        {loading ? (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton.Card key={i} height={80} />
              ))}
            </div>
          </div>
        ) : (
          <CategoryGrid categories={topCategories} />
        )}

        {/* Coupons */}
        <CouponsStrip stores={stores} />

        {/* Deals (≥ 15% off) */}
        <DealsStrip minDiscount={15} limit={12} />

        {/* Buy again — only renders when there are hits */}
        {isAuthenticated && user?.role === 'customer' && <BuyAgainRail />}

        {/* Nearby stores */}
        <NearbyStoresRail stores={stores} loading={loading} limit={5} />

        {/* Trending */}
        <TrendingNowRail limit={12} />

        {/* Trust pillars */}
        <QuickHighlights />
      </Container>
    </div>
  );
};


export default CustomerHome;

